/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ColDef, ColumnState} from 'ag-grid-community';
import isNil from 'lodash.isnil';
import {getSearchtFilter} from '@ion/applications/src/globalsearch/GlobalSearchUtils';
import {useGridRefreshAction} from '@ion/applications/src/hooks/useGridRefreshAction';
import {translate} from '@ion/core/src/locale/locale';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  AccountLink,
  Asset,
  Assets,
  CRUDPermissions,
  GlobalSearchModule
} from '@ion/core/src/types/entities';
import {DownloadTemplate} from '@ion/core/src/types/templates';
import {joinByObjectProperty} from '@ion/core/src/utils/ArrayUtil';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {itemsToCSV} from '@ion/core/src/utils/StringUtil';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {getUniqItems} from '@ion/dls/src/editors/ListEditorUtils';
import {
  defaultDateFilterParams,
  defaultStatusFilterParams,
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {definitionsToState} from '@ion/dls/src/grid/ColumnResolver';
import {ExportClient} from '@ion/dls/src/grid/Exporter';
import {
  BulkHeader,
  GridOperation,
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {
  SelectAllCheckbox,
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import {useGridEventListener} from '@ion/dls/src/grid/useGridEventListener';
import {useBulkOperationResults} from '@ion/dls/src/hooks/useBulkOperationResults';
import {useCRUDOperationResults} from '@ion/dls/src/hooks/useCRUDOperationResults';
import {GridPageSizeProps, ViewSubGroupsProps} from '@ion/dls/src/types/types';
import {translateStatus} from '@ion/dls/src/utils/ComponentUtils';
import {
  determineColumnFilter,
  GridFilterType,
  processExportColumns
} from '@ion/dls/src/utils/GridUtils';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {useGroupRefresh} from '../../../hooks/useGroupRefresh';
import {exportAssets} from '../../../store/actions/exporter';
import {dispatchGridColumnsChange, sortColumns} from '../../../store/helpers';
import {gridDefaultPageSize} from '../../../utils/helpers';
import DeviceGroupLinks from '../../devices/DeviceGroupLinks';
import DeviceLink from '../../devices/DeviceLink';
import {AccountCell} from '../accounts/AccountCell';
import {GridOperations} from '../AdminContainer';
import {AdminAssetsState, viewAdminAsset} from '../assets/state';
import {getAssociations} from './AdminAssetsUtils';
import {AssetRowEditor} from './AssetRowEditor';
import {AssetsActionMenu} from './AssetsActionMenu';
import {modularSearchSelector} from './selector';
import {
  adminAssetsGridRefreshed,
  deleteAdminAssets,
  editAdminAssets,
  getAdminAssets,
  NAME,
  SET_GRID_COLUMNS
} from './state';

interface AdminAssetsGridProps extends
  ViewSubGroupsProps,
  SelectAllCheckbox,
  GridOperations,
  GridPageSizeProps,
  BulkHeader,
  GridOperation {
  onSelectAccount?: (value: AccountLink) => void;
  adminAssets?: AdminAssetsState;
  isCtcAdmin?: boolean;
  setItemSelected?: Function;
  includeGlobalSearch?: boolean;
  searchKeyword?: string;
  searchModule?: string;
  selectedAssets?: Assets;
  setSelectedAssets?: Function;
  filteredColumns?: AssetsGridColumns[];
  enableSelectionsExport?: boolean;
  setTotalRowsCount?: Function;
  areTotalRowsSelected?: boolean;
  assetsPermissions?: CRUDPermissions;
  accountsPermissions?: CRUDPermissions;
  refreshInitialization?: Function;
  showTimeoutError?: boolean;
  hasExportLimit?: boolean;
  totalRowsCount?: number;
  selectedItemsCount?: number;
  exportLimit?: number;
  setSortOn?: Function;
  Features?: ApplicationFeatures;
}

export enum AssetsGridColumns {
  NAME = 'name',
  ALIAS = 'alias',
  TYPE = 'type',
  CATEGORY = 'assetCategory',
  SCHEDULE = 'schedule',
  DEVICE = 'device',
  ACCOUNT = 'group',
  STATUS = 'status',
  IO_PROFILE = 'ioProfileUI',
  VIN = 'vin',
  LICENSE_NUMBER = 'licenseNumber',
  LICENSE_STATE = 'licenseState',
  MAINTENENCE_SCHEDULES = 'maintenanceSchedules',
  OPERATORS = 'operatorsUI',
  ROUTES = 'routes',
  CREATED = 'createdOnUI',
  LAST_MODIFIED = 'lastModifiedOnUI',
  ODOMETER = 'odometerReference',
  ODOMETER_TIME = 'odometerReferenceTimeUI',
  ENGINE_HOURS = 'engineHoursReference',
  ENGINE_HOURS_TIME = 'engineHoursReferenceTimeUI',
  GROUPS = 'groupNames',
  INSTALL_DATE = 'installDateUI',
  UNINSTALL_DATE = 'uninstallDateUI',
  YEAR = 'year',
  MAKE = 'make',
  MODEL = 'model'
}

const DEFAULT_FILTERED_COLUMNS = [
  AssetsGridColumns.NAME,
  AssetsGridColumns.TYPE,
  AssetsGridColumns.ACCOUNT,
  AssetsGridColumns.STATUS,
  AssetsGridColumns.ALIAS,
  AssetsGridColumns.VIN,
  AssetsGridColumns.LICENSE_NUMBER,
  AssetsGridColumns.CREATED,
  AssetsGridColumns.LAST_MODIFIED
];

/**
 * AdminAssetsGrid
 *
 * Grid to be rendered on the Admin Assets page
 */
export const AdminAssetsGrid = ({
  adminAssets,
  assetsPermissions,
  selectedAssets,
  setSelectedAssets,
  onBulkActionsClick,
  accountsPermissions,
  setItemSelected,
  onSelectAccount,
  setTotalRowsCount,
  getAreTotalRowsSelected,
  areTotalRowsSelected,
  toggleViewSubGroups,
  viewSubGroups,
  setGridPageSize,
  refreshInitialization,
  setSortOn,
  Features,
  gridPageSize = gridDefaultPageSize,
  filteredColumns = DEFAULT_FILTERED_COLUMNS,
  searchKeyword = '',
  searchModule = '',
  includeGlobalSearch = false,
  showBulkHeader = false,
  isCtcAdmin = false,
  usePreferedPageSize = false,
  enableSelectionsExport = false,
  hideSubGroups = false,
  isPersist = false,
  enableSelectAllCheckbox = false,
  isSortByAlphaNumeric = false,
  showTimeoutError = false,
  hasExportLimit = false,
  totalRowsCount = 0,
  selectedItemsCount = 0,
  exportLimit = 0
}: AdminAssetsGridProps, ref) => {
  const dispatch                   = useDispatch();
  const modularSearchParams        = useSelector(modularSearchSelector);
  const columnState: ColumnState[] = useSelector(({adminAssets: {grid}}: AdminAssetsGridProps) => {
    return grid.columnState;
  });

  const {
    addResult,
    editResult,
    updateResult,
    deleteResult
  } = useCRUDOperationResults('adminAssets');

  const {
    bulkEditResult,
    bulkDeleteResult,
    bulkImportResult,
    bulkMoveResult
  } = useBulkOperationResults('adminAssets');

  const [refreshRequired, setRefreshRequired] = useState(false);
  const ionGridRef                            = useRef<IonGridAPI>();
  const refreshGrid                           = useGroupRefresh();
  const gridExportCount                       = areTotalRowsSelected ? totalRowsCount
    : selectedItemsCount || totalRowsCount;

  const assetColumns = useMemo(() => (
    translate('admin-assets:grid.columns', {returnObjects: true})
  ), []);

  const defaultColumnDefinitions = useMemo(() =>
    getColumnDefinitions({
      isCtcAdmin,
      includeGlobalSearch,
      searchModule,
      filteredColumns,
      isSortByAlphaNumeric,
      assetColumns
    }),
  [isCtcAdmin, includeGlobalSearch, searchModule, filteredColumns]);

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : defaultColumnDefinitions,
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  const onSelectionChanged = useCallback((event) => {
    const rowsSelected = event.api.getSelectedRows();
    setSelectedAssets(getUniqItems(rowsSelected));
    setItemSelected(!!rowsSelected.length);
  }, [setItemSelected]);

  useGridRefreshAction(refreshRequired, adminAssetsGridRefreshed);

  useGridEventListener(ionGridRef, 'selectionChanged', onSelectionChanged);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    getExportColumnDefinitions,
    onExport,
    onRefresh,
    getSelection
  }));

  const onRefresh = function() {
    setRefreshRequired(true);
    setSelectedAssets([]);
  };

  const getSelection = function() {
    return ionGridRef.current?.getSelectedColumns();
  };

  const onColumnSelectionsUpdate = function(columns: ColDef[]): void {
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS) as any);
  };

  const onColumnStateUpdate = function(state: ColumnState[]): void {
    if (!includeGlobalSearch) {
      dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS) as any);
    }
  };

  const getExportColumnDefinitions = useCallback(() => {
    const ionGridData         = ionGridRef?.current?.getExportParams();
    const {columns, sortOn}   = ionGridData || {};
    const customColumnBuilder = {};
    const nameMappingColumns  = {
      alias                      : 'externalId',
      type                       : 'assetType',
      category                   : 'assetCategory',
      createdOnUI                : 'createdOn',
      device                     : 'devices',
      groupNames                 : 'groups',
      lastModifiedOnUI           : 'lastModifiedOn',
      installDateUI              : 'installDate',
      uninstallDateUI            : 'uninstallDate',
      odometerReferenceTimeUI    : 'odometerReferenceTime',
      engineHoursReferenceTimeUI : 'engineHoursReferenceTime'
    };
    columns?.forEach(column => {
      if (column.headerName) {
        const fieldLabel = nameMappingColumns[column.field] || attributeMappings[column.field] || column.field;
        customColumnBuilder[fieldLabel] = column.headerName;
      }
    });
    return {columns: customColumnBuilder, sortOn};
  }, []);

  const getActionMenu = useMemo(() => (
    <AssetsActionMenu selectedAssets={selectedAssets}
      disableAction={!assetsPermissions?.hasUpdatePermission}
      areTotalRowsSelected={areTotalRowsSelected} />
  ), [areTotalRowsSelected, selectedAssets]);

  const attributeMappings = {
    group            : 'account',
    alias            : 'externalId',
    type             : 'assetType',
    createdOnUI      : 'createdOn',
    lastModifiedOnUI : 'lastModifiedOn'
  };

  const defaultSort: SortModelType = {
    colId : 'name',
    sort  : 'asc'
  };

  useEffect(() => {
    const refresh = Boolean(
      deleteResult ||
      editResult ||
      bulkEditResult ||
      bulkDeleteResult ||
      bulkImportResult ||
      addResult ||
      updateResult ||
      bulkMoveResult
    );
    setRefreshRequired(refresh);
  }, [
    deleteResult,
    editResult,
    bulkEditResult,
    bulkDeleteResult,
    bulkImportResult,
    addResult,
    updateResult,
    bulkMoveResult
  ]);

  useEffect(() => {
    if (includeGlobalSearch && !isNil(searchKeyword)) {
      setRefreshRequired(!refreshRequired);
    }
  }, [includeGlobalSearch, searchKeyword]);

  const getFilter = (filter) => {
    if (includeGlobalSearch) {
      const field = searchModule === GlobalSearchModule.AssetsVin ? 'vin' : 'name';
      return getSearchtFilter(field, searchKeyword, filter);
    }
    return filter;
  };

  const callbackInterceptor = useCallback((callback: Function): (result, options) => void => {
    return (result, options) => {
      setTotalRowsCount(result?.totalCount);
      callback?.(result, options);
    };
  }, []);

  function getAssets({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) {
    setSortOn(sort);
    dispatch(getAdminAssets({
      filter               : getFilter(filter),
      callback             : callbackInterceptor(callback),
      includeSubGroups     : viewSubGroups,
      includeAccountFilter : !includeGlobalSearch,
      includeGroups        : true,
      pageId               : page + 1, // CTC uses pages starting from 1
      pageSize,
      showTimeoutError,
      sort
    }) as any);
  };

  function onEditAssets(assets: Asset[]) {
    dispatch(editAdminAssets(assets));
  };

  function onDeleteAssets(assets: Asset[]) {
    dispatch(deleteAdminAssets(assets));
  };

  const onExport = function(downloadParams?: DownloadTemplate) {
    const params: ExportClient = ionGridRef.current?.getExportParams(
      viewSubGroups,
      enableSelectionsExport,
      createModularSearchFilters(modularSearchParams)
    );
    if (downloadParams?.columns) {
      params.columns = processExportColumns(downloadParams?.columns);
    }
    if (params?.filterOn?.groups === -1) {
      params.filterOn.groups = null;
    }
    params.exportLimit = exportLimit;
    params.gridExportCount = gridExportCount;
    params.areTotalRowsSelected = areTotalRowsSelected;
    const exportParams = {
      ...params,
      includeGroups : true
    };
    dispatch(exportAssets(
      exportParams,
      downloadParams?.columns ? downloadParams : undefined,
      hasExportLimit
    ) as any);
  };

  const onAssetClick = useCallback(asset => {
    return function() {
      dispatch(viewAdminAsset(entityId(asset), true, true) as any);
    };
  }, []);

  return (
    <IonGrid ref={ionGridRef}
      showBulkHeader={showBulkHeader}
      paginationEnabled
      showCustomPaginationBar
      pageSize={gridPageSize}
      isPersist={isPersist}
      onBulkActionsClick={onBulkActionsClick}
      setGridPageSize={setGridPageSize}
      refreshRequired={refreshRequired || (refreshGrid && !includeGlobalSearch)}
      setRefreshRequired={setRefreshRequired}
      loadItems={getAssets}
      augmentItems={constructAssetGridRows}
      attributeMappings={attributeMappings}
      checkboxesEnabled={!includeGlobalSearch}
      onBulkEdit={onEditAssets}
      onBulkDelete={onDeleteAssets}
      disableBulkDelete={areTotalRowsSelected || !assetsPermissions?.hasDeletePermission}
      disableBulkEdit={areTotalRowsSelected || !assetsPermissions?.hasUpdatePermission}
      disableBulkActions={!assetsPermissions?.hasUpdatePermission}
      actionMenuItems={isCtcAdmin ? getActionMenu : null}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      onColumnChooserSave={onColumnSelectionsUpdate}
      onColumnsUpdated={onColumnStateUpdate}
      onColumnChooserClose={function() {
        toggleColumnChooser(false);
      }}
      columnComponents={{
        rowMenu : item => (
          <AssetRowEditor asset={item?.data}
            includeGlobalSearch={includeGlobalSearch}
            assetsPermissions={assetsPermissions} />
        ),
        accountName : (item: GridResult) => (
          <AccountCell item={item}
            isCtcAdmin={isCtcAdmin}
            Features={Features}
            accountsPermissions={accountsPermissions}
            refreshInitialization={refreshInitialization}
            onSelectAccount={onSelectAccount} />
        ),
        assetNameCell : item => <LinkCell data={item}
          textTransform={'none'}
          onClick={onAssetClick(item?.data)} />,
        deviceCell : ({data}: any) => (
          <DeviceLink data={data}
            preventParentEventTrigger={false}
            isEditMode={false} />
        ),
        groupsUI : DeviceGroupLinks
      }}
      pageSizes={PAGE_SIZES}
      hideSubGroups={hideSubGroups}
      toggleViewSubGroups={!includeGlobalSearch ? toggleViewSubGroups : undefined}
      viewSubGroups={viewSubGroups}
      defaultSort={defaultSort}
      getAreTotalRowsSelected={getAreTotalRowsSelected}
      isTotalRowsSelected={areTotalRowsSelected}
      enableSelectAllCheckbox={enableSelectAllCheckbox}
      gridOptions={{onSelectionChanged}}
      usePreferedPageSize={usePreferedPageSize} />
  );
};

/**
 *
 * Takes a list of assets and constructs a list that
 * can be easily consumed by the IonGrid API
 */
export const constructAssetGridRows = (assets: Asset[]): GridResult[] => {
  return assets.map(asset => ({
    ...asset,
    group                      : asset?.account?.title,
    name                       : asset?.name,
    alias                      : asset?.externalId,
    type                       : asset?.assetType || '',
    category                   : asset?.assetCategory,
    schedule                   : asset?.schedules?.[0]?.title,
    device                     : itemsToCSV(asset?.devices, 'title') || '',
    devices                    : asset?.devices || [],
    associations               : getAssociations(asset),
    status                     : translateStatus(asset?.status),
    assetId                    : asset?.id,
    operatorsUI                : joinByObjectProperty(asset.operators, 'title') || '',
    ioProfileUI                : asset?.ioProfile?.title,
    maintenanceSchedules       : asset?.extensions?.scheduleName || '',
    createdOnUI                : formatDateTime(asset?.createdOn),
    lastModifiedOnUI           : formatDateTime(asset?.lastModifiedOn),
    odometerReferenceTimeUI    : formatDateTime(asset?.odometerReferenceTime),
    engineHoursReferenceTimeUI : formatDateTime(asset?.engineHoursReferenceTime),
    routes                     : asset?.route?.title,
    associateDeviceEsns        : itemsToCSV(asset?.devices, 'esn') || '',
    groupNames                 : itemsToCSV(asset?.groups, 'title') || '',
    installDateUI              : formatDateTime(asset?.installDate),
    uninstallDateUI            : formatDateTime(asset?.uninstallDate)
  }));
};

const ctcAdminRemoveColumns = [
  'alias',
  'routes',
  'schedule',
  'ioProfileUI',
  'operatorsUI',
  'maintenanceSchedules'
];

const filterColumns = (columns: ColDef[], isCtcAdmin: boolean, isSortByAlphaNumeric: boolean) => {
  const column = isSortByAlphaNumeric ? sortColumns(columns) : columns;
  if (isCtcAdmin) {
    columns = column.filter(col => !ctcAdminRemoveColumns.includes(col.field));
  }
  return columns;
};

const defaultColumn = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : true
});

/**
 *
 * Returns column definitions for desktop admin assets grid
 */
const getColumnDefinitionsDesktop = ({
  isCtcAdmin,
  filteredColumns,
  isSortByAlphaNumeric,
  assetColumns
}: ColumnDefinitionFlags): ColDef[] => {
  const columns = [{
    headerName         : translate('admin-assets:grid.columns.name'),
    field              : AssetsGridColumns.NAME,
    cellRenderer       : 'assetNameCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : determineColumnFilter(AssetsGridColumns.NAME, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.alias'),
    field        : AssetsGridColumns.ALIAS,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.ALIAS, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.type'),
    field        : AssetsGridColumns.TYPE,
    cellRenderer : 'assetType',
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.TYPE, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.category'),
    field      : AssetsGridColumns.CATEGORY,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-assets:grid.columns.schedule'),
    field      : AssetsGridColumns.SCHEDULE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName         : translate('admin-assets:grid.columns.device'),
    field              : AssetsGridColumns.DEVICE,
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  },
  {
    headerName         : translate('admin-assets:grid.columns.group'),
    field              : AssetsGridColumns.ACCOUNT,
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : determineColumnFilter(AssetsGridColumns.ACCOUNT, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.status'),
    field        : AssetsGridColumns.STATUS,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.STATUS, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultStatusFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.ioProfile'),
    field      : AssetsGridColumns.IO_PROFILE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName   : translate('admin-assets:grid.columns.vin'),
    field        : AssetsGridColumns.VIN,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.VIN, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultStatusFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.licenseNumber'),
    field        : AssetsGridColumns.LICENSE_NUMBER,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.LICENSE_NUMBER, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.licenseState'),
    field      : AssetsGridColumns.LICENSE_STATE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.maintenanceSchedules'),
    field      : AssetsGridColumns.MAINTENENCE_SCHEDULES,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.operators'),
    field      : AssetsGridColumns.OPERATORS,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.routes'),
    field      : AssetsGridColumns.ROUTES,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName   : translate('admin-assets:grid.columns.createdOn'),
    field        : AssetsGridColumns.CREATED,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.CREATED, filteredColumns, GridFilterType.DATE),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultDateFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.lastModifiedOn'),
    field        : AssetsGridColumns.LAST_MODIFIED,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.LAST_MODIFIED, filteredColumns, GridFilterType.DATE),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultDateFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.odometerReference'),
    field      : AssetsGridColumns.ODOMETER,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.odometerReferenceTime'),
    field      : AssetsGridColumns.ODOMETER_TIME,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.engineHoursReference'),
    field      : AssetsGridColumns.ENGINE_HOURS,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.engineHoursReferenceTime'),
    field      : AssetsGridColumns.ENGINE_HOURS_TIME,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName         : translate('admin-assets:grid.columns.groups'),
    field              : AssetsGridColumns.GROUPS,
    cellRenderer       : 'groupsUI',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : true
  }];
  if (isCtcAdmin) {
    columns.push(
      defaultColumn(assetColumns.year, AssetsGridColumns.YEAR),
      defaultColumn(assetColumns.make, AssetsGridColumns.MAKE),
      defaultColumn(assetColumns.model, AssetsGridColumns.MODEL),
      defaultColumn(assetColumns.installDate, AssetsGridColumns.INSTALL_DATE),
      defaultColumn(assetColumns.uninstallDate, AssetsGridColumns.UNINSTALL_DATE)
    );
  }
  return filterColumns(columns, isCtcAdmin, isSortByAlphaNumeric);
};

/**
 *
 * Returns column definitions for mobile admin assets grid
 */
const getColumnDefinitionsMobile = ({
  isCtcAdmin,
  filteredColumns,
  isSortByAlphaNumeric,
  assetColumns
}: ColumnDefinitionFlags): ColDef[] => {
  const columns = [{
    headerName         : translate('admin-assets:grid.columns.name'),
    field              : AssetsGridColumns.NAME,
    cellRenderer       : 'assetNameCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : determineColumnFilter(AssetsGridColumns.NAME, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.alias'),
    field        : AssetsGridColumns.ALIAS,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.ALIAS, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.type'),
    field        : AssetsGridColumns.TYPE,
    cellRenderer : 'assetType',
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.TYPE, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.category'),
    field      : AssetsGridColumns.CATEGORY,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.schedule'),
    field      : AssetsGridColumns.SCHEDULE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName         : translate('admin-assets:grid.columns.device'),
    field              : AssetsGridColumns.DEVICE,
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName         : translate('admin-assets:grid.columns.group'),
    field              : AssetsGridColumns.ACCOUNT,
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : determineColumnFilter(AssetsGridColumns.ACCOUNT, filteredColumns),
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.status'),
    field        : AssetsGridColumns.STATUS,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.STATUS, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultStatusFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.ioProfile'),
    field      : AssetsGridColumns.IO_PROFILE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName   : translate('admin-assets:grid.columns.vin'),
    field        : AssetsGridColumns.VIN,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.VIN, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.licenseNumber'),
    field        : AssetsGridColumns.LICENSE_NUMBER,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.LICENSE_NUMBER, filteredColumns),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.licenseState'),
    field      : AssetsGridColumns.LICENSE_STATE,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.maintenanceSchedules'),
    field      : AssetsGridColumns.MAINTENENCE_SCHEDULES,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.operators'),
    field      : AssetsGridColumns.OPERATORS,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.routes'),
    field      : AssetsGridColumns.ROUTES,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName   : translate('admin-assets:grid.columns.createdOn'),
    field        : AssetsGridColumns.CREATED,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.CREATED, filteredColumns, GridFilterType.DATE),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultDateFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.lastModifiedOn'),
    field        : AssetsGridColumns.LAST_MODIFIED,
    sortable     : true,
    filter       : determineColumnFilter(AssetsGridColumns.LAST_MODIFIED, filteredColumns, GridFilterType.DATE),
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultDateFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.odometerReference'),
    field      : AssetsGridColumns.ODOMETER,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.odometerReferenceTime'),
    field      : AssetsGridColumns.ODOMETER_TIME,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.engineHoursReference'),
    field      : AssetsGridColumns.ENGINE_HOURS,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName : translate('admin-assets:grid.columns.engineHoursReferenceTime'),
    field      : AssetsGridColumns.ENGINE_HOURS_TIME,
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : true
  },
  {
    headerName         : translate('admin-assets:grid.columns.groups'),
    field              : AssetsGridColumns.GROUPS,
    cellRenderer       : 'groupsUI',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : false,
    hide               : true
  }];
  if (isCtcAdmin) {
    columns.push(
      defaultColumn(assetColumns.year, AssetsGridColumns.YEAR),
      defaultColumn(assetColumns.make, AssetsGridColumns.MAKE),
      defaultColumn(assetColumns.model, AssetsGridColumns.MODEL),
      defaultColumn(assetColumns.installDate, AssetsGridColumns.INSTALL_DATE),
      defaultColumn(assetColumns.uninstallDate, AssetsGridColumns.UNINSTALL_DATE)
    );
  }
  return filterColumns(columns, isCtcAdmin, isSortByAlphaNumeric);
};

/**
 * Returns column definitions for the admin asset grid in global search desktop mode.
 */
const getColumnDefinitionsDesktopSearch = (searchModule): ColDef[] => {
  return [{
    headerName         : translate('admin-assets:grid.columns.name'),
    field              : AssetsGridColumns.NAME,
    cellRenderer       : 'assetNameCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.vin'),
    field        : AssetsGridColumns.VIN,
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : searchModule === GlobalSearchModule.Assets,
    filterParams : defaultStatusFilterParams
  },
  {
    headerName   : translate('admin-assets:grid.columns.lastModifiedOn'),
    field        : AssetsGridColumns.LAST_MODIFIED,
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultDateFilterParams
  },
  {
    headerName         : translate('admin-assets:grid.columns.devices'),
    field              : AssetsGridColumns.DEVICE,
    cellRenderer       : 'deviceCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  },
  {
    headerName   : translate('admin-assets:grid.columns.status'),
    field        : AssetsGridColumns.STATUS,
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultStatusFilterParams
  },
  {
    headerName         : translate('admin-assets:grid.columns.group'),
    field              : AssetsGridColumns.ACCOUNT,
    cellRenderer       : 'accountName',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultTextFilterParams
  }];
};

type ColumnDefinitionFlags = GridOperations & {
  filteredColumns: AssetsGridColumns[];
  isCtcAdmin?: boolean;
  includeGlobalSearch?: boolean;
  searchModule?: string;
  assetColumns?: object;
}

const getColumnDefinitions = ({
  isCtcAdmin,
  includeGlobalSearch,
  searchModule,
  filteredColumns,
  isSortByAlphaNumeric,
  assetColumns
}: ColumnDefinitionFlags) => ({
  desktop : includeGlobalSearch
    ? getColumnDefinitionsDesktopSearch(searchModule)
    : getColumnDefinitionsDesktop({isCtcAdmin, filteredColumns, isSortByAlphaNumeric, assetColumns}),
  mobile : includeGlobalSearch
    ? getColumnDefinitionsDesktopSearch(searchModule)
    : getColumnDefinitionsMobile({isCtcAdmin, filteredColumns, isSortByAlphaNumeric, assetColumns})
});

export default forwardRef(AdminAssetsGrid);
